<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

// Shared condition-row editor (ConditionStep legs + flow trigger conditions).
// Rows are mutated in place through the (stable) passed array, same contract
// as NodeSettings and the rest of the builder; the server-side evaluator
// stays authoritative. From phase C every non-first row carries a join
// operator: 'or' starts a new AND-group.
const props = defineProps({
  conditions: { type: Array, required: true },
  canAdd: { type: Boolean, default: true },
  lifecycleEnabled: { type: Boolean, default: false },
  lifecycleStages: { type: Array, default: () => [] },
});
const { t } = useI18n();

const rows = computed(() => props.conditions);

const addCondition = () =>
  rows.value.push({
    attribute_key: 'variable',
    variable: '',
    filter_operator: 'equal_to',
    values: '',
    query_operator: 'and',
  });

const removeCondition = index => rows.value.splice(index, 1);

// Two attributes need a second key from the user: a run variable name, and a
// contact custom-attribute name.
const keyedAttribute = condition =>
  ['variable', 'contact_attribute'].includes(condition.attribute_key);

const lifecycleAttributeKeys = [
  'contact_lifecycle_stage',
  'contact_lifecycle_stage_name',
  'contact_lifecycle_category',
];
const showLifecycleAttributes = condition =>
  props.lifecycleEnabled ||
  lifecycleAttributeKeys.includes(condition.attribute_key);
const lifecycleStageAttribute = condition =>
  ['contact_lifecycle_stage', 'contact_lifecycle_stage_name'].includes(
    condition.attribute_key
  );
const resetValueForAttribute = condition => {
  condition.values = '';
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      v-for="(condition, index) in conditions"
      :key="index"
      class="flex flex-col gap-1"
    >
      <select
        v-if="index > 0"
        v-model="condition.query_operator"
        class="w-24 mb-0 self-start"
      >
        <option value="and">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.JOIN_AND') }}
        </option>
        <option value="or">
          {{ t('TELETEL_FLOWS.BUILDER.FORM.JOIN_OR') }}
        </option>
      </select>
      <div class="flex flex-col gap-1 p-2 border rounded-lg border-n-weak">
        <select
          v-model="condition.attribute_key"
          class="mb-0"
          @change="resetValueForAttribute(condition)"
        >
          <option value="variable">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.VARIABLE_NAME') }}
          </option>
          <option value="inbox_id">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_INBOX') }}
          </option>
          <option value="last_incoming_message">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_LAST_MESSAGE') }}
          </option>
          <option value="source">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_SOURCE') }}
          </option>
          <option value="channel_type">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CHANNEL') }}
          </option>
          <option value="business_hours">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_BUSINESS_HOURS') }}
          </option>
          <option value="day_of_week">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_DAY_OF_WEEK') }}
          </option>
          <option value="time_of_day">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_TIME_OF_DAY') }}
          </option>
          <option value="contact_name">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONTACT_NAME') }}
          </option>
          <option value="contact_email">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONTACT_EMAIL') }}
          </option>
          <option value="contact_phone_number">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONTACT_PHONE') }}
          </option>
          <option value="contact_country">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONTACT_COUNTRY') }}
          </option>
          <option value="contact_city">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONTACT_CITY') }}
          </option>
          <option value="contact_language">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONTACT_LANGUAGE') }}
          </option>
          <option value="contact_attribute">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONTACT_ATTRIBUTE') }}
          </option>
          <option value="contact_is_returning">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONTACT_RETURNING') }}
          </option>
          <option value="contact_conversations_count">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONTACT_CONVERSATIONS') }}
          </option>
          <template v-if="showLifecycleAttributes(condition)">
            <option value="contact_lifecycle_stage">
              {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_LIFECYCLE_STAGE') }}
            </option>
            <option value="contact_lifecycle_stage_name">
              {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_LIFECYCLE_STAGE_NAME') }}
            </option>
            <option value="contact_lifecycle_category">
              {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_LIFECYCLE_CATEGORY') }}
            </option>
          </template>
          <option value="conversation_labels">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONVERSATION_LABELS') }}
          </option>
          <option value="conversation_priority">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONVERSATION_PRIORITY') }}
          </option>
          <option value="conversation_status">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONVERSATION_STATUS') }}
          </option>
          <option value="conversation_assigned">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONVERSATION_ASSIGNED') }}
          </option>
          <option value="conversation_team_id">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONVERSATION_TEAM') }}
          </option>
          <option value="conversation_age_minutes">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.ATTR_CONVERSATION_AGE') }}
          </option>
        </select>
        <input
          v-if="keyedAttribute(condition)"
          v-model="condition.variable"
          class="mb-0"
          :placeholder="
            condition.attribute_key === 'variable'
              ? t('TELETEL_FLOWS.BUILDER.FORM.VARIABLE_NAME')
              : t('TELETEL_FLOWS.BUILDER.FORM.ATTRIBUTE')
          "
        />
        <select v-model="condition.filter_operator" class="mb-0">
          <option value="equal_to">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.EQUAL') }}
          </option>
          <option value="not_equal_to">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.NOT_EQUAL') }}
          </option>
          <option value="contains">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.OP_CONTAINS') }}
          </option>
          <option value="not_contains">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.OP_NOT_CONTAINS') }}
          </option>
          <option value="starts_with">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.OP_STARTS_WITH') }}
          </option>
          <option value="greater_than">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.OP_GREATER_THAN') }}
          </option>
          <option value="less_than">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.OP_LESS_THAN') }}
          </option>
          <option value="in">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.OP_IN') }}
          </option>
          <option value="is_present">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.OP_IS_PRESENT') }}
          </option>
          <option value="is_blank">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.OP_IS_BLANK') }}
          </option>
        </select>
        <select
          v-if="lifecycleStageAttribute(condition)"
          v-model="condition.values"
          class="mb-0 text-start"
        >
          <option value="">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_PICK_STAGE') }}
          </option>
          <option
            v-for="stage in lifecycleStages"
            :key="stage.id"
            :value="
              condition.attribute_key === 'contact_lifecycle_stage'
                ? String(stage.id)
                : stage.name
            "
          >
            {{ stage.emoji ? `${stage.emoji} ` : '' }}{{ stage.name }}
          </option>
        </select>
        <select
          v-else-if="condition.attribute_key === 'contact_lifecycle_category'"
          v-model="condition.values"
          class="mb-0 text-start"
        >
          <option value="active">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_CATEGORY_ACTIVE') }}
          </option>
          <option value="won">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_CATEGORY_WON') }}
          </option>
          <option value="lost">
            {{ t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_CATEGORY_LOST') }}
          </option>
        </select>
        <input
          v-else
          v-model="condition.values"
          class="mb-0"
          :placeholder="t('TELETEL_FLOWS.BUILDER.FORM.COMPARE_VALUE')"
        />
        <button
          type="button"
          class="text-xs text-n-ruby-11 text-start"
          @click="removeCondition(index)"
        >
          {{ t('TELETEL_FLOWS.BUILDER.FORM.REMOVE_CONDITION') }}
        </button>
      </div>
    </div>
    <button
      v-if="canAdd"
      type="button"
      class="text-n-brand text-start"
      @click="addCondition"
    >
      {{ `+ ${t('TELETEL_FLOWS.BUILDER.FORM.ADD_CONDITION')}` }}
    </button>
  </div>
</template>
